import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export type YearType = "Financial" | "Calendar";
export type DeductionType = "NPS" | "SUPR";

export interface DeductionMasterItem {
  id?: number;
  deduction_type?: string;
  description?: string;
  [key: string]: any;
}

export interface NpsRecord {
  id?: number;
  empId?: string;
  year?: string;
  month?: string;
  savingType?: string;
  forc?: string;
  employeeContribution?: string;
  employerContribution?: string;
  requestStatus?: string;
  errorMsg?: string;
  [key: string]: any;
}

export interface MonthRecord {
  month: string;
  employeeContr: string;
  employerContr: string;
}

export interface NpsTotals {
  employeeContr: string;
  employerContr: string;
}

export interface NpsFetchResult {
  months: MonthRecord[];
  total: NpsTotals;
}

function unwrapList(res: any): any[] {
  const root = res?.data ?? res;
  const raw = root?.data ?? root;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.list)) return raw.list;
  if (Array.isArray(raw?.records)) return raw.records;
  if (Array.isArray(root)) return root;
  if (Array.isArray(res)) return res;
  return [];
}

function toError(err: any, fallback: string): Error {
  const msg =
    err?.response?.data?.data?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;
  return new Error(typeof msg === "string" ? msg : fallback);
}

function pick(record: any, keys: string[], fallback = ""): string {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return fallback;
}

export function savingTypeFor(deduction: DeductionType): string {
  return deduction === "NPS" ? "NPS" : "SUPR";
}

export function forcFor(yearType: YearType): string {
  return yearType === "Calendar" ? "C" : "F";
}

export function formatAmount(value: any): string {
  const num = parseFloat(String(value ?? "0").replace(/,/g, ""));
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
}

function matchesSavingType(record: any, expected: string): boolean {
  const value = pick(record, ["savingType", "SavingType", "saving_type"]).toUpperCase();
  if (!value) return true;
  if (expected === "NPS") return value === "NPS" || value === "N";
  if (expected === "SUPR") return value === "SUPR" || value === "P" || value === "SUP";
  return value === expected.toUpperCase();
}

function matchesForc(record: any, expected: string): boolean {
  const value = pick(record, ["forc", "Forc", "FORC", "yearType"]).toUpperCase();
  if (!value) return true;
  if (expected === "C") return value === "C" || value === "CALENDAR";
  if (expected === "F") return value === "F" || value === "FINANCIAL";
  return value === expected.toUpperCase();
}

function matchesYear(record: any, year: string): boolean {
  const value = pick(record, ["year", "Year", "YEAR"]);
  if (!value) return true;
  return value === year;
}

function isTotalRow(record: any): boolean {
  return pick(record, ["month", "Month", "MONTH"]).toLowerCase() === "total";
}

/** Calendar: Jan–Dec. Financial (SAP FY): Apr–Mar. */
const MONTH_INDEX: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

function monthSortKey(month: string, yearType: YearType): number {
  const raw = month.trim().toLowerCase();
  let idx = MONTH_INDEX[raw] ?? MONTH_INDEX[raw.slice(0, 3)];
  if (idx === undefined) {
    const num = parseInt(raw.replace(/\D/g, ""), 10);
    if (Number.isFinite(num) && num >= 1 && num <= 12) idx = num - 1;
  }
  if (idx === undefined) return 99;
  if (yearType === "Financial") {
    return (idx - 3 + 12) % 12;
  }
  return idx;
}

function sortMonths(months: MonthRecord[], yearType: YearType): MonthRecord[] {
  return [...months].sort(
    (a, b) => monthSortKey(a.month, yearType) - monthSortKey(b.month, yearType)
  );
}

function toMonthRecord(record: any): MonthRecord {
  return {
    month: pick(record, ["month", "Month", "MONTH"], "-"),
    employeeContr: formatAmount(
      pick(record, ["employeeContribution", "EmployeeContribution", "empContribution"])
    ),
    employerContr: formatAmount(
      pick(record, ["employerContribution", "EmployerContribution", "emprContribution"])
    ),
  };
}

export async function fetchDeductionMaster(): Promise<DeductionMasterItem[]> {
  try {
    const res: any = await Api.post(API_URLS.ANNUAL_NPS_PF.NPS_DEDUCTION_MASTER, {
      type: "fetch",
    });
    const root = res?.data ?? res;
    if (res?.success === false || root?.success === false) {
      return [];
    }
    return unwrapList(res);
  } catch {
    return [];
  }
}

export async function fetchNpsRequests(
  year: string,
  yearType: YearType,
  deductionType: DeductionType
): Promise<NpsFetchResult> {
  const payload = {
    year,
    savingType: savingTypeFor(deductionType),
    forc: forcFor(yearType),
  };

  try {
    const res: any = await Api.post(API_URLS.ANNUAL_NPS_PF.NPS_GET_REQUESTS, payload);
    const root = res?.data ?? res;
    if (res?.success === false || root?.success === false) {
      throw new Error(res?.message || root?.message || "Failed to fetch NPS details");
    }

    const records: NpsRecord[] = unwrapList(res);
    const filtered = records.filter(
      (row) =>
        matchesYear(row, year) &&
        matchesSavingType(row, payload.savingType) &&
        matchesForc(row, payload.forc)
    );
    const usable = filtered.length > 0 ? filtered : records;

    const totalRow = usable.find(isTotalRow);
    const months = sortMonths(
      usable.filter((row) => !isTotalRow(row)).map(toMonthRecord),
      yearType
    );

    const total: NpsTotals = totalRow
      ? {
          employeeContr: formatAmount(
            pick(totalRow, ["employeeContribution", "EmployeeContribution", "empContribution"])
          ),
          employerContr: formatAmount(
            pick(totalRow, ["employerContribution", "EmployerContribution", "emprContribution"])
          ),
        }
      : {
          employeeContr: months
            .reduce((sum, row) => sum + parseFloat(row.employeeContr), 0)
            .toFixed(2),
          employerContr: months
            .reduce((sum, row) => sum + parseFloat(row.employerContr), 0)
            .toFixed(2),
        };

    return { months, total };
  } catch (err: any) {
    throw toError(err, "Something went wrong");
  }
}
